import { useCallback } from 'react';
import { isAndroid } from 'react-device-detect';
import { useGenerateDocumentHTML } from './use-generate-document-html';

const CLEANUP_DELAY_MS = 120 * 1000;
const PRINT_TRIGGER_DELAY_MS = 50;
const PRINT_DIALOG_INIT_DELAY_MS = 200;

function printViaWindow(html: string) {
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    return;
  }

  const cleanup = () => {
    printWindow.close();
  };

  const triggerPrint = () => {
    try {
      printWindow.focus();
      printWindow.print();
      setTimeout(cleanup, PRINT_DIALOG_INIT_DELAY_MS);
    } catch (error) {
      console.error('Print error:', error);
      cleanup();
    }
  };

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  if (printWindow.document.readyState === 'complete') {
    setTimeout(triggerPrint, PRINT_TRIGGER_DELAY_MS);
  } else {
    printWindow.addEventListener('load', () => {
      setTimeout(triggerPrint, PRINT_TRIGGER_DELAY_MS);
    });
  }
}

function printViaIframe(html: string) {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = 'none';
  iframe.style.visibility = 'hidden';

  document.body.appendChild(iframe);

  let cleanupTimer: ReturnType<typeof setTimeout> | undefined;

  const cleanup = () => {
    iframe.remove();
    clearTimeout(cleanupTimer);
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    cleanup();
    return;
  }

  iframe.onload = () => {
    try {
      iframe.contentWindow?.addEventListener('afterprint', cleanup, {
        once: true
      });
      cleanupTimer = setTimeout(cleanup, CLEANUP_DELAY_MS);
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch (error) {
      console.error('Print error:', error);
      cleanup();
    }
  };

  iframe.srcdoc = html;
}

export function usePrintDocument() {
  const generateHTML = useGenerateDocumentHTML();

  return useCallback(() => {
    const html = generateHTML();
    const printFunction = isAndroid ? printViaWindow : printViaIframe;
    printFunction(html);
  }, [generateHTML]);
}
